/**
 * Client contracts and their drawdown: the usage tracker.
 *
 * A contract is a team's monetary commitment over a span of days, with a
 * discount while the commitment lasts. Contracts are mirrored read-only from
 * Backoffice; everything else here — what was drawn, what remains, the
 * discount on each item — is computed by the server on read, in the contract's
 * own currency.
 *
 * Only billable work in the contract's currency draws down. Items excluded
 * from billing, proof of concept or R&D, unpriced, or in another currency are
 * listed with their status but draw nothing.
 */

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number.parseFloat(value);
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  return value === null || value === undefined ? null : toNumber(value);
}

export type DrawdownItemStatus =
  | 'eligible'
  | 'excluded'
  | 'non_commercial'
  | 'unpriced'
  | 'other_currency';

export const ITEM_STATUS_LABELS: Readonly<Record<DrawdownItemStatus, string>> = {
  eligible: 'Drawn',
  excluded: 'Excluded from billing',
  non_commercial: 'POC / R&D',
  unpriced: 'Unpriced',
  other_currency: 'Other currency',
};

/** Backoffice's discount reasons, which the Hub's drawdown mirrors. */
export type DiscountReason =
  | 'CONTRACT_TERMS'
  | 'OVER_USAGE_AMOUNT'
  | 'NO_CONTRACT_TERMS'
  | 'NO_DISCOUNT'
  | 'EXCEPTIONAL_CIRCUMSTANCES';

export const DISCOUNT_REASON_LABELS: Readonly<Record<DiscountReason, string>> = {
  CONTRACT_TERMS: 'Within commitment',
  // The crossing item is discounted in full but already carries this reason.
  OVER_USAGE_AMOUNT: 'Over commitment',
  NO_CONTRACT_TERMS: 'No contract',
  NO_DISCOUNT: 'No discount',
  EXCEPTIONAL_CIRCUMSTANCES: 'Exceptional circumstances',
};

export type ConsumptionSource = 'hub_report' | 'backoffice_project';

// --- Summary ---

export interface ContractSummary {
  id: string;
  backofficeContractId: number;
  teamId: string;
  clientName: string | null;
  /** Null when Backoffice holds no usable currency; nothing can then draw down. */
  currency: string | null;
  commitmentAmount: number;
  /** A fraction: 0.1 is ten per cent. */
  discountRate: number;
  startsOn: string;
  endsOn: string;
  exceptionalCircumstances: boolean;
  /** What an item draws: its gross price, or what the client pays. */
  basis: 'gross' | 'net';
  consumed: number;
  remaining: number;
  overage: number;
  gross: number;
  discount: number;
  net: number;
  /** The item that took consumption past the commitment. */
  crossingItemId: string | null;
  counts: Partial<Record<DrawdownItemStatus, number>>;
  /** Amounts that could not draw down, by currency code. */
  otherCurrencyTotals: Record<string, number>;
  /** Other contracts of the team whose windows overlap this one. */
  overlappingContractIds: string[];
  mirroredAt: string;
}

export interface ApiContractSummary {
  id: string;
  backoffice_contract_id: number;
  team_id: string;
  client_name: string | null;
  currency: string | null;
  commitment_amount: string | number;
  discount_rate: string | number;
  starts_on: string;
  ends_on: string;
  exceptional_circumstances: boolean;
  basis: 'gross' | 'net';
  consumed: string | number;
  remaining: string | number;
  overage: string | number;
  gross: string | number;
  discount: string | number;
  net: string | number;
  crossing_item_id: string | null;
  counts: Partial<Record<DrawdownItemStatus, number>>;
  other_currency_totals: Record<string, string | number>;
  overlapping_contract_ids: string[];
  mirrored_at: string;
}

export function transformToContractSummary(api: ApiContractSummary): ContractSummary {
  return {
    id: api.id,
    backofficeContractId: api.backoffice_contract_id,
    teamId: api.team_id,
    clientName: api.client_name,
    currency: api.currency,
    commitmentAmount: toNumber(api.commitment_amount),
    discountRate: toNumber(api.discount_rate),
    startsOn: api.starts_on,
    endsOn: api.ends_on,
    exceptionalCircumstances: api.exceptional_circumstances,
    basis: api.basis,
    consumed: toNumber(api.consumed),
    remaining: toNumber(api.remaining),
    overage: toNumber(api.overage),
    gross: toNumber(api.gross),
    discount: toNumber(api.discount),
    net: toNumber(api.net),
    crossingItemId: api.crossing_item_id,
    counts: api.counts ?? {},
    otherCurrencyTotals: Object.fromEntries(
      Object.entries(api.other_currency_totals ?? {}).map(([code, v]) => [code, toNumber(v)])
    ),
    overlappingContractIds: api.overlapping_contract_ids ?? [],
    mirroredAt: api.mirrored_at,
  };
}

/** Share of the commitment consumed, 0 to 1 (capped); 0 for a zero commitment. */
export function consumedShare(summary: ContractSummary): number {
  if (summary.commitmentAmount <= 0) {
    return 0;
  }
  return Math.min(summary.consumed / summary.commitmentAmount, 1);
}

/** Where a contract's window stands relative to `today` (an ISO date). */
export function contractPeriod(
  summary: Pick<ContractSummary, 'startsOn' | 'endsOn'>,
  today: string
): 'current' | 'upcoming' | 'ended' {
  if (summary.endsOn < today) {
    return 'ended';
  }
  return summary.startsOn > today ? 'upcoming' : 'current';
}

// --- Detail ---

export interface DrawdownLine {
  itemId: string;
  source: ConsumptionSource;
  sourceRef: string;
  reportId: string | null;
  /** What HR quote on an invoice line as the project ID. */
  backofficeProjectId: string | null;
  projectCode: string | null;
  /** The client's own case number. */
  clientReference: string | null;
  label: string | null;
  description: string | null;
  consumedAt: string;
  amount: number | null;
  currency: string | null;
  isExcludedFromBilling: boolean;
  isProofOfConcept: boolean;
  isResearchAndDevelopment: boolean;
  status: DrawdownItemStatus;
  /** Set on drawn lines only. */
  reason: DiscountReason | null;
  discountRate: number;
  discountAmount: number;
  netAmount: number | null;
  remainingBefore: number | null;
  drawn: number;
  overage: number;
}

export interface ApiDrawdownLine {
  item_id: string;
  source: ConsumptionSource;
  source_ref: string;
  report_id: string | null;
  backoffice_project_id: string | null;
  project_code: string | null;
  client_reference: string | null;
  label: string | null;
  description: string | null;
  consumed_at: string;
  amount: string | number | null;
  currency: string | null;
  is_excluded_from_billing: boolean;
  is_proof_of_concept: boolean;
  is_research_and_development: boolean;
  status: DrawdownItemStatus;
  reason: DiscountReason | null;
  discount_rate: string | number;
  discount_amount: string | number;
  net_amount: string | number | null;
  remaining_before: string | number | null;
  drawn: string | number;
  overage: string | number;
}

export function transformToDrawdownLine(api: ApiDrawdownLine): DrawdownLine {
  return {
    itemId: api.item_id,
    source: api.source,
    sourceRef: api.source_ref,
    reportId: api.report_id,
    backofficeProjectId: api.backoffice_project_id,
    projectCode: api.project_code,
    clientReference: api.client_reference,
    label: api.label,
    description: api.description,
    consumedAt: api.consumed_at,
    amount: toNullableNumber(api.amount),
    currency: api.currency,
    isExcludedFromBilling: api.is_excluded_from_billing,
    isProofOfConcept: api.is_proof_of_concept,
    isResearchAndDevelopment: api.is_research_and_development,
    status: api.status,
    reason: api.reason,
    discountRate: toNumber(api.discount_rate),
    discountAmount: toNumber(api.discount_amount),
    netAmount: toNullableNumber(api.net_amount),
    remainingBefore: toNullableNumber(api.remaining_before),
    drawn: toNumber(api.drawn),
    overage: toNumber(api.overage),
  };
}

export interface ContractActivity {
  /** Subjects monitored at some point in the window. */
  monitoredSubjects: number;
  /** Completed screening-batch reports in the window, per batch. */
  batches: { batchId: string; label: string; reports: number }[];
}

export interface ContractDetail extends ContractSummary {
  /** Every item in the window, drawn or listed, in the order drawn. */
  lines: DrawdownLine[];
  activity: ContractActivity;
}

export interface ApiContractDetail extends ApiContractSummary {
  lines: ApiDrawdownLine[];
  activity: {
    monitored_subjects: number;
    batches: { batch_id: string; label: string; reports: number }[];
  };
}

export function transformToContractDetail(api: ApiContractDetail): ContractDetail {
  return {
    ...transformToContractSummary(api),
    lines: api.lines.map(transformToDrawdownLine),
    activity: {
      monitoredSubjects: api.activity.monitored_subjects,
      batches: api.activity.batches.map((b) => ({
        batchId: b.batch_id,
        label: b.label,
        reports: b.reports,
      })),
    },
  };
}

// --- Reconciliation ---

export type ReconClass =
  | 'MATCH'
  | 'EXPECTED_CURRENCY'
  | 'EXPECTED_FLAG'
  | 'EXPECTED_END_DAY'
  | 'EXPECTED_ADJUSTMENT'
  | 'EXPECTED_DELETED'
  | 'STALE_DJANGO'
  | 'PRICE_DRIFT'
  | 'CONTRACT_MISMATCH'
  | 'UNEXPLAINED';

export const RECON_CLASS_LABELS: Readonly<Record<ReconClass, string>> = {
  MATCH: 'Match',
  EXPECTED_CURRENCY: 'Expected: currency',
  EXPECTED_FLAG: 'Expected: billing flag',
  EXPECTED_END_DAY: 'Expected: last day',
  EXPECTED_ADJUSTMENT: 'Expected: client adjustment',
  EXPECTED_DELETED: 'Expected: deleted',
  STALE_DJANGO: 'Backoffice not recomputed',
  PRICE_DRIFT: 'Price differs',
  CONTRACT_MISMATCH: 'Different contract',
  UNEXPLAINED: 'Unexplained',
};

/** How much attention a class needs: an expected difference is known, a
 * stale or drifted one needs action, an unexplained one needs looking into. */
export function reconSeverity(cls: ReconClass): 'ok' | 'expected' | 'action' | 'unexplained' {
  if (cls === 'MATCH') {
    return 'ok';
  }
  if (cls.startsWith('EXPECTED_')) {
    return 'expected';
  }
  return cls === 'UNEXPLAINED' ? 'unexplained' : 'action';
}

export interface ReconRow {
  /** Null for Hub work with no Backoffice project. */
  projectId: number | null;
  projectCode: string | null;
  source: string | null;
  completedDate: string;
  classification: ReconClass;
  note: string;
  hubContractId: number | null;
  djangoContractId: number | null;
  hubStatus: DrawdownItemStatus | null;
  hubAmount: number | null;
  hubCurrency: string | null;
  djangoFinalPrice: number | null;
  djangoCurrency: string | null;
  djangoAdjustments: number | null;
  hubDiscountRate: number | null;
  djangoDiscountRate: number | null;
  hubDiscount: number | null;
  djangoDiscount: number | null;
  hubNet: number | null;
  djangoNet: number | null;
  hubReason: string | null;
  djangoReason: string | null;
  hubPartnerFees: number | null;
  djangoPartnerFees: number | null;
  partnerFeesMatch: boolean | null;
  hubItem: string | null;
}

type ApiNumber = string | number | null;

export interface ApiReconRow {
  project_id: number | null;
  project_code: string | null;
  source: string | null;
  completed_date: string;
  classification: ReconClass;
  note: string;
  hub_contract_id: number | null;
  django_contract_id: number | null;
  hub_status: DrawdownItemStatus | null;
  hub_amount: ApiNumber;
  hub_currency: string | null;
  django_final_price: ApiNumber;
  django_currency: string | null;
  django_adjustments: ApiNumber;
  hub_discount_rate: ApiNumber;
  django_discount_rate: ApiNumber;
  hub_discount: ApiNumber;
  django_discount: ApiNumber;
  hub_net: ApiNumber;
  django_net: ApiNumber;
  hub_reason: string | null;
  django_reason: string | null;
  hub_partner_fees: ApiNumber;
  django_partner_fees: ApiNumber;
  partner_fees_match: boolean | null;
  hub_item: string | null;
}

export function transformToReconRow(api: ApiReconRow): ReconRow {
  return {
    projectId: api.project_id,
    projectCode: api.project_code,
    source: api.source,
    completedDate: api.completed_date,
    classification: api.classification,
    note: api.note,
    hubContractId: api.hub_contract_id,
    djangoContractId: api.django_contract_id,
    hubStatus: api.hub_status,
    hubAmount: toNullableNumber(api.hub_amount),
    hubCurrency: api.hub_currency,
    djangoFinalPrice: toNullableNumber(api.django_final_price),
    djangoCurrency: api.django_currency,
    djangoAdjustments: toNullableNumber(api.django_adjustments),
    hubDiscountRate: toNullableNumber(api.hub_discount_rate),
    djangoDiscountRate: toNullableNumber(api.django_discount_rate),
    hubDiscount: toNullableNumber(api.hub_discount),
    djangoDiscount: toNullableNumber(api.django_discount),
    hubNet: toNullableNumber(api.hub_net),
    djangoNet: toNullableNumber(api.django_net),
    hubReason: api.hub_reason,
    djangoReason: api.django_reason,
    hubPartnerFees: toNullableNumber(api.hub_partner_fees),
    djangoPartnerFees: toNullableNumber(api.django_partner_fees),
    partnerFeesMatch: api.partner_fees_match,
    hubItem: api.hub_item,
  };
}

export interface ContractReconciliation {
  contractId: number;
  hubConsumed: number;
  /** Backoffice's cached usage figure, as fresh as its last recompute. */
  djangoGrossUsage: number | null;
  counts: Partial<Record<ReconClass, number>>;
  rows: ReconRow[];
}

export interface ApiContractReconciliation {
  contract_id: number;
  hub_consumed: string | number;
  django_gross_usage: ApiNumber;
  counts: Partial<Record<ReconClass, number>>;
  rows: ApiReconRow[];
}

export function transformToContractReconciliation(
  api: ApiContractReconciliation
): ContractReconciliation {
  return {
    contractId: api.contract_id,
    hubConsumed: toNumber(api.hub_consumed),
    djangoGrossUsage: toNullableNumber(api.django_gross_usage),
    counts: api.counts ?? {},
    rows: api.rows.map(transformToReconRow),
  };
}

// --- Line-item export ---

/** A text cell as a spreadsheet will read it: one that starts like a formula
 * is prefixed so it is shown, not run. Client case numbers are typed in by
 * clients, and this file is opened in Excel. */
function textCell(value: string | null | undefined): string {
  const text = value ?? '';
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

function numberCell(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export const CONTRACT_LINES_CSV_HEADER = [
  'Consumed on',
  'Client case no.',
  'Project ID',
  'Project code',
  'Item',
  'Description',
  'Status',
  'Amount',
  'Currency',
  'Discount rate',
  'Discount',
  'Net',
  'Drawn',
  'Overage',
  'Reason',
];

/** The line items of a contract, header first, as `downloadCsvFile` takes them. */
export function contractLinesCsv(detail: ContractDetail): string[][] {
  return [
    CONTRACT_LINES_CSV_HEADER,
    ...detail.lines.map((line) => [
      line.consumedAt.slice(0, 10),
      textCell(line.clientReference),
      textCell(line.backofficeProjectId),
      textCell(line.projectCode),
      textCell(line.label),
      textCell(line.description),
      ITEM_STATUS_LABELS[line.status] ?? line.status,
      numberCell(line.amount),
      line.currency ?? '',
      line.reason ? numberCell(line.discountRate) : '',
      line.reason ? numberCell(line.discountAmount) : '',
      numberCell(line.netAmount),
      numberCell(line.drawn),
      numberCell(line.overage),
      line.reason ? (DISCOUNT_REASON_LABELS[line.reason] ?? line.reason) : '',
    ]),
  ];
}

/** Backoffice could not be read, so there is nothing to reconcile against.
 * Distinct from a failure of the Hub's own: the figures here are fine, the
 * comparison is unavailable until Backoffice answers. */
export class BackofficeUnreadableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackofficeUnreadableError';
  }
}
